import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const GREEN = '#004D35';
const INK = '#173127';
const PAPER = '#f5f3ef';
const LINE = '#e3e6df';
const emptyProfile = {
  first_name: '', last_name: '', professional_title: '', headline: '', bio: '', location: '', category_id: '',
  booking_link: '', website_url: '', linkedin_url: '', remote_available: false, specialties: [],
};
const fields = [
  ['first_name', 'First name', true],
  ['last_name', 'Last name', true],
  ['professional_title', 'Professional title', true],
  ['headline', 'Profile headline', true],
  ['bio', 'About you', true],
  ['location', 'Location', true],
];

export default function Onboarding() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(emptyProfile);
  const [photoPreview, setPhotoPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [published, setPublished] = useState(false);
  const [categories, setCategories] = useState([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [specialtiesText, setSpecialtiesText] = useState('');
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'advisor') {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadError('');
    Promise.all([
      fetch('/api/advisor/me', { credentials: 'include', headers: getAuthHeader() }).then(async response => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'We could not load your saved profile.');
        return body.advisor || body;
      }),
      fetch('/api/reference/categories', { credentials: 'include', headers: getAuthHeader() })
        .then(async response => {
          if (!response.ok) throw new Error('Professional categories could not be loaded. Please retry.');
          return response.json();
        }),
    ]).then(([advisor, categoryData]) => {
      if (!active) return;
      setProfile(current => ({ ...current, ...(advisor.application || {}), ...advisor,
        booking_link: advisor.booking_link || advisor.calendly_url || '' }));
      setSpecialtiesText((advisor.specialties || []).join(', '));
      setPublished(Boolean(advisor.is_visible && ['approved', 'active'].includes(advisor.status)));
      setCategories(Array.isArray(categoryData) ? categoryData : categoryData.categories || []);
      setAcceptTerms(!!advisor.terms_accepted_at);
      const src = advisor.profile_photo_url || advisor.profile_photo;
      if (src) setPhotoPreview(src.startsWith('http') || src.startsWith('/') ? src : `/api/upload/${src}`);
    }).catch(err => { if (active) setLoadError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [getAuthHeader, user, reload]);

  const update = (key, value) => setProfile(current => ({ ...current, [key]: value }));
  const saveDraft = async () => {
    setSaving(true); setError(''); setNotice('');
    try {
      const response = await fetch('/api/advisor/me', {
        method: 'PUT', credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Your draft could not be saved. Try again.');
      setProfile(current => ({ ...current, ...(data.advisor || data) }));
      setNotice(published ? 'Your public profile has been updated.' : 'Draft saved. You can return to it any time.');
    } catch (err) { setError(err.message || 'Your draft could not be saved.'); }
    finally { setSaving(false); }
  };
  const uploadPhoto = async file => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { setError('Choose a JPG, PNG, or WebP image.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Choose an image smaller than 5 MB.'); return; }
    setError('');
    setNotice('');
    setUploadingPhoto(true);
    const data = new FormData();
    data.append('photo', file);
    try {
      const response = await fetch('/api/advisor/me/photo', {
        method: 'POST', credentials: 'include', headers: getAuthHeader(), body: data,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Photo upload failed. Try again.');
      const advisor = result.advisor || result;
      if (result.photo_url || advisor.profile_photo_url || advisor.profile_photo) {
        setPhotoPreview(result.photo_url || advisor.profile_photo_url || advisor.profile_photo);
      }
      setNotice('Photo uploaded.');
    } catch (err) { setError(err.message); }
    finally { setUploadingPhoto(false); }
  };
  const publish = async () => {
    if (!profile.terms_accepted_at && !acceptTerms) {
      setError('Please accept the advisor terms before your first publication.');
      return;
    }
    setPublishing(true); setError(''); setNotice('');
    try {
      const saveResponse = await fetch('/api/advisor/me', {
        method: 'PUT', credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      const saved = await saveResponse.json().catch(() => ({}));
      if (!saveResponse.ok) throw new Error(saved.error || 'Save your profile before publishing.');
      const response = await fetch('/api/advisor/me/publish', {
        method: 'POST', credentials: 'include', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ accept_terms: acceptTerms }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Your profile could not be published. Check the required fields and try again.');
      setProfile(current => ({ ...current, ...(saved.advisor || saved), ...(data.advisor || data) }));
      setPublished(true);
      setNotice('Your profile is published and ready to share.');
    } catch (err) { setError(err.message); }
    finally { setPublishing(false); }
  };

  if (loading) return <main style={{ minHeight: '100dvh', background: PAPER, padding: 24 }}><div aria-label="Loading profile" style={{ maxWidth: 720, margin: '72px auto', height: 240, borderRadius: 20, background: '#e8ebe5' }} /></main>;
  if (loadError) return <main style={{ minHeight: '100dvh', background: PAPER, padding: '12vh 24px', color: INK, textAlign: 'center' }}>
    <h1>Profile setup could not load</h1><p role="alert">{loadError}</p>
    <button onClick={() => setReload(value => value + 1)} style={{ padding: '12px 24px', background: GREEN, color: '#fff', border: 0, borderRadius: 999 }}>Retry</button>
  </main>;
  if (user?.role !== 'advisor') return (
    <main style={{ minHeight: '100dvh', background: PAPER, padding: '12vh 24px', color: INK, textAlign: 'center', fontFamily: 'sans-serif' }}>
      <h1 style={{ fontFamily: 'Georgia,serif' }}>Advisor profile setup</h1>
      <p>This setup is available to approved advisor accounts. Contact the Viysr team if you need help.</p>
      <Link to="/" style={{ color: GREEN }}>Return home</Link>
    </main>
  );

  const inputStyle = { width: '100%', border: `1px solid ${LINE}`, borderRadius: 10, background: '#fff', padding: '12px 14px', color: INK, font: 'inherit', boxSizing: 'border-box' };
  return (
    <main style={{ minHeight: '100dvh', background: PAPER, color: INK, fontFamily: '"Plus Jakarta Sans", sans-serif', paddingBottom: 48 }}>
      <header style={{ height: 64, background: '#fff', borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 max(20px, calc((100vw - 1060px) / 2))' }}>
        <Link to="/" style={{ textDecoration: 'none', color: GREEN, fontFamily: 'Georgia,serif', fontWeight: 700, fontSize: 21 }}>Viysr</Link>
        <button type="button" onClick={() => navigate('/advisor/dashboard')} style={{ border: 0, background: 'none', color: GREEN, cursor: 'pointer', font: 'inherit' }}>Advisor dashboard</button>
      </header>
      <div style={{ maxWidth: 1060, margin: '38px auto', padding: '0 20px', display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(260px, .7fr)', gap: 24 }}>
        <section style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 18, padding: 'clamp(22px, 4vw, 38px)' }}>
          <div style={{ color: GREEN, textTransform: 'uppercase', letterSpacing: '.12em', fontSize: 11, fontWeight: 800, marginBottom: 10 }}>Profile setup</div>
          <h1 style={{ font: '700 clamp(26px, 4vw, 36px)/1.1 Georgia,serif', margin: '0 0 10px' }}>Make your expertise easy to find.</h1>
          <p style={{ color: '#68746c', lineHeight: 1.65, margin: '0 0 28px' }}>Add the essentials, save a draft, then publish when you are ready.</p>
          {error && <div role="alert" style={{ padding: 13, marginBottom: 18, borderRadius: 10, background: '#fff1ee', color: '#9f3023', border: '1px solid #f0c9c1' }}>{error}</div>}
          {notice && <div role="status" style={{ padding: 13, marginBottom: 18, borderRadius: 10, background: '#edf6ef', color: GREEN, border: '1px solid #c9dfce' }}>{notice}</div>}
          {fields.map(([key, label, required]) => <label key={key} style={{ display: 'block', marginBottom: 17, fontSize: 13, fontWeight: 700 }}>
            {label}{required && <span aria-hidden="true" style={{ color: '#a64c3e' }}> *</span>}
            {key === 'bio' ? <textarea required value={profile[key] || ''} onChange={e => update(key, e.target.value)} rows={5} style={{ ...inputStyle, display: 'block', marginTop: 7, resize: 'vertical', lineHeight: 1.55 }} /> :
              <input required value={profile[key] || ''} onChange={e => update(key, e.target.value)} style={{ ...inputStyle, display: 'block', marginTop: 7 }} />}
          </label>)}
          <label style={{ display: 'block', marginBottom: 17, fontSize: 13, fontWeight: 700 }}>Professional category *
            <select value={profile.category_id || ''} onChange={e => update('category_id', e.target.value ? Number(e.target.value) : '')} style={{ ...inputStyle, display: 'block', marginTop: 7 }}>
              <option value="">Select a category</option>
              {categories.map(category => <option key={category.id} value={category.id}>{category.display_name || category.name || category.label}</option>)}
            </select>
          </label>
          <label style={{ display: 'block', marginBottom: 17, fontSize: 13, fontWeight: 700 }}>Booking calendar link · optional
            <input type="url" value={profile.booking_link || ''} onChange={e => update('booking_link', e.target.value)} placeholder="https://calendly.com/your-name" style={{ ...inputStyle, display: 'block', marginTop: 7 }} />
            <span style={{ display: 'block', marginTop: 6, fontWeight: 400, color: '#68746c', lineHeight: 1.5 }}>Clients will open your calendar directly. Calendly, Cal.com, Google appointment schedules, and other booking links work.</span>
          </label>
          {['website_url', 'linkedin_url'].map(key => <label key={key} style={{ display: 'block', marginBottom: 17, fontSize: 13, fontWeight: 700 }}>
            {key === 'website_url' ? 'Website' : 'LinkedIn profile'} · optional
            <input type="url" value={profile[key] || ''} onChange={e => update(key, e.target.value)} placeholder="https://" style={{ ...inputStyle, display: 'block', marginTop: 7 }} />
          </label>)}
          <label style={{ display: 'block', marginBottom: 17, fontSize: 13, fontWeight: 700 }}>Specialties · optional
            <input value={specialtiesText} onChange={e => { setSpecialtiesText(e.target.value); update('specialties', e.target.value.split(',').map(v => v.trim()).filter(Boolean)); }} placeholder="Tax planning, payroll, business valuation" style={{ ...inputStyle, display: 'block', marginTop: 7 }} />
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, fontSize: 13 }}>
            <input type="checkbox" checked={!!profile.remote_available} onChange={e => update('remote_available', e.target.checked)} />
            I can work with clients remotely.
          </label>
          <label style={{ display: 'block', margin: '24px 0', fontSize: 13, fontWeight: 700 }}>
            Profile photo <span style={{ color: '#78847d', fontWeight: 400 }}>· optional</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploadingPhoto || saving || publishing} onChange={e => uploadPhoto(e.target.files?.[0])} style={{ display: 'block', marginTop: 9, maxWidth: '100%' }} />
            {uploadingPhoto && <span role="status">Uploading photo…</span>}
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingTop: 12 }}>
            <button type="button" onClick={saveDraft} disabled={saving || publishing || uploadingPhoto} style={{ background: '#fff', border: `1px solid ${GREEN}`, color: GREEN, borderRadius: 999, padding: '12px 20px', fontWeight: 700, cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : published ? 'Save changes' : 'Save draft'}</button>
            <button type="button" onClick={() => setPreviewing(v => !v)} style={{ background: '#edf1eb', border: 0, color: INK, borderRadius: 999, padding: '12px 20px', fontWeight: 700, cursor: 'pointer' }}>{previewing ? 'Close preview' : 'Preview profile'}</button>
            <button type="button" onClick={publish} disabled={publishing || saving || uploadingPhoto || published} style={{ background: GREEN, border: 0, color: '#fff', borderRadius: 999, padding: '12px 22px', fontWeight: 700, cursor: 'pointer', opacity: publishing || published ? .65 : 1 }}>{published ? 'Published' : publishing ? 'Publishing…' : 'Publish profile'}</button>
          </div>
          {!published && <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 18, color: '#56645b', fontSize: 13, lineHeight: 1.55 }}>
            <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} style={{ accentColor: GREEN, marginTop: 3 }} />
            <span>I agree to the <Link to="/terms" target="_blank" style={{ color: GREEN, fontWeight: 700 }}>Viysr terms</Link>. This is required to publish my profile.</span>
          </label>}
        </section>
        <aside style={{ alignSelf: 'start', background: '#e9efe8', borderRadius: 18, padding: 24, position: 'sticky', top: 20 }}>
          <div style={{ fontSize: 11, color: GREEN, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{previewing ? 'Live preview' : published ? 'Published profile' : 'Draft profile'}</div>
          <div style={{ width: 76, height: 76, borderRadius: '50%', overflow: 'hidden', background: GREEN, color: '#fff', display: 'grid', placeItems: 'center', margin: '20px 0 16px', fontSize: 24, fontFamily: 'Georgia,serif' }}>{photoPreview ? <img alt="" src={photoPreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`}</div>
          <h2 style={{ font: '700 24px/1.15 Georgia,serif', margin: 0 }}>{profile.first_name || 'Your'} {profile.last_name || 'name'}</h2>
          <p style={{ color: GREEN, fontWeight: 700, margin: '8px 0 5px' }}>{profile.professional_title || 'Professional title'}</p>
          <p style={{ color: '#57675c', lineHeight: 1.5, margin: '0 0 10px' }}>{profile.location || 'Your location'}</p>
          <p style={{ font: 'italic 17px/1.45 Georgia,serif', margin: '18px 0', color: INK }}>{profile.headline || 'A clear headline helps clients understand your work.'}</p>
          <p style={{ color: '#57675c', lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0 }}>{profile.bio || 'Your short introduction will appear here.'}</p>
          <p style={{ fontSize: 12, color: '#718078', borderTop: '1px solid #d3ded4', paddingTop: 15, marginTop: 22 }}>Required fields are marked with an asterisk. {published ? 'Saving changes updates your public profile.' : 'Save draft does not publish your profile.'}</p>
        </aside>
      </div>
      <style>{'@media(max-width:760px){main>div:nth-of-type(1){grid-template-columns:1fr!important} main aside{position:static!important}}'}</style>
    </main>
  );
}
